import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DeviationSourceItem, DeviationType } from '../deviation-settings/deviation-settings.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { Item } from '@app/core/components/listbox/listbox.component';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateEditDialogComponent, TranslateEditDialogData } from '@app/modal/translate-edit-dialog/translate-edit-dialog.component';

@Component({
    selector: 'tt-deviation-type',
    templateUrl: './deviation-type.component.html',
    styleUrls: ['./deviation-type.component.css'],
})
export class DeviationTypeComponent implements OnChanges {
    @Input()
    public deviationTypeKeyno: string = '';

    public deviationType?: DeviationType;

    public activityTypes: Item[] = [];

    public deviationSources: DeviationSourceItem[] = [];

    public loading = false;

    public saving = false;

    public deleting = false;

    selectedtab = '';

    nameButtons: FormButton[] = [
        {
            id: 'translate',
            icon: 'far fa-language',
            type: 'primary',
            onClick: () => this.openTranslationDialog(),
        },
    ];

    @Output()
    public onSave = new EventEmitter<DeviationType>();

    @Output()
    public onDelete = new EventEmitter<DeviationType>();

    constructor(private datatask: DataTaskService, private dialog: MatDialog) {}

    public async openTranslationDialog() {
        if (!!this.deviationType) {
            const edits = await this.datatask.Post(3550, { deviationtype_keyno: this.deviationType.deviationtype_keyno });
            const dialogRef = this.dialog.open(TranslateEditDialogComponent, {
                width: '120rem',
                maxWidth: '95vw',
                height: '80vh',
                data: <TranslateEditDialogData<DeviationTypeLangEdit>>{
                    heading: `(${this.deviationType.deviationtype_keyno}) ${this.deviationType.deviationtype_name}`,
                    textToTranslate: this.deviationType.deviationtype_name,
                    edits: edits,
                    save: async (data) => {
                        await this.datatask.Post(3551, { edits: data.edits });
                        await this.loadDeviationType();
                        dialogRef.close();
                        this.onSave.emit(this.deviationType);
                    },
                    wordNameKey: 'deviationtype_name',
                    wordIdKey: 'deviationtype_keyno',
                },
            });
        }
    }

    public async save() {
        if (!!this.deviationType) {
            try {
                this.saving = true;

                const response = (await this.datatask.Post(1230, this.deviationType))[0];

                if (response.errorcode === '0') {
                    this.onSave.emit(this.deviationType);
                }
            } finally {
                this.saving = false;
            }
        }
    }

    public async delete() {
        if (this.deviationTypeKeyno > '0' && !!this.deviationType) {
            try {
                this.deleting = true;

                await this.datatask.Post(1231, this.deviationType);

                this.onDelete.emit(this.deviationType);
            } finally {
                this.deleting = false;
            }
        }
    }

    public onActivityTypeRelationChanged(item: { [key: string]: any }) {
        item['isactive'] = item['is_selected'] === true ? '1' : '0';

        this.updateActivityTypeRelation({ activitytypedevtype_keyno: item['item_id'], isactive: item['isactive'] });
    }

    public onAllActivityTypesChecked(event: boolean) {
        const itemsToUpdate = this.activityTypes.filter((item) => item['isactive'] !== (event === true ? '1' : '0'));

        itemsToUpdate.map((item) => this.onActivityTypeRelationChanged(item));
    }

    public onDeviationSourceRelationChanged(item: any) {
        item.is_active = item['is_selected'] === true ? '1' : '0';

        this.updateDeviationSource({ deviationtype_keyno: this.deviationType!.deviationtype_keyno, deviationsource_keyno: item.deviationsource_keyno, is_active: item.is_active });
    }

    public onAllDeviationSourceChecked(event: boolean) {
        const itemsToUpdate = this.deviationSources.filter((item) => item.is_active !== (event === true ? '1' : '0'));

        itemsToUpdate.map((item) => this.onDeviationSourceRelationChanged(item));
    }

    private async loadDeviationType() {
        if (!!this.deviationTypeKeyno) {
            this.deviationType = (await this.datatask.Post(1229, { deviationtype_keyno: this.deviationTypeKeyno ?? '0' }))[0];
        }
    }

    private async loadActivityTypes() {
        if (this.deviationTypeKeyno > '0') {
            this.activityTypes = (await this.datatask.Post(1235, { argtype: 'deviationtype_keyno', argvalue: this.deviationTypeKeyno })).map((item: Item) => ({ ...item, is_selected: item['isactive'] === '1' ? true : false }));
        }
    }

    private async updateActivityTypeRelation(params: { activitytypedevtype_keyno: string; isactive: '1' | '0' }) {
        return this.datatask.Post(1237, params);
    }

    private async loadDeviationSourcs() {
        if (this.deviationTypeKeyno > '0') {
            this.deviationSources = (await this.datatask.Post(3537, { deviationtype_keyno: this.deviationTypeKeyno })).map((item: DeviationSourceItem) => ({ ...item, is_selected: item.is_active === '1' ? true : false }));
        }
    }

    private async updateDeviationSource(params: { deviationtype_keyno: string; deviationsource_keyno: string; is_active: '1' | '0' }) {
        return this.datatask.Post(3539, params);
    }

    private async loadData() {
        try {
            this.loading = true;
            await Promise.all([this.loadDeviationType(), this.loadActivityTypes(), this.loadDeviationSourcs()]);
        } finally {
            this.loading = false;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['deviationTypeKeyno']?.currentValue && changes['deviationTypeKeyno'].currentValue !== changes['deviationTypeKeyno'].previousValue) {
            this.loadData();
        }
    }
}

interface DeviationTypeLangEdit extends Record<string, string> {
    deviationtype_keyno: string;
    deviationtype_name: string;
    language_id: string;
    language_name: string;
    orderby: string;
    google_language_id: string;
}
